import React from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import {BasicIconButton} from './basic-icon-button.component';
import {SIZES, SIZES_CLASSES} from './constants';
import styles from './icon-button.component.module.styl';

import type {BasicIconButtonProps} from './basic-icon-button.component';


const classNames = withCssModule(styles);

export interface IconButtonProps extends BasicIconButtonProps {
    /**
     * Touch-target size preset. Use `SIZES.L` or `SIZES.XL` for larger
     * hit areas in toolbars or primary actions. Defaults to `SIZES.NONE`
     * (compact, default sizing).
     */
    size?: SIZES,
    /**
     * When `true`, applies a selected/toggled visual style and sets
     * `aria-pressed="true"` so screen readers announce the toggle state.
     * Defaults to `undefined` (no toggle semantics).
     */
    selected?: boolean,
    /**
     * When `true`, renders the button without a background or border — a
     * ghost/flat appearance suitable for toolbars and inline actions.
     */
    flat?: boolean,
}

/**
 * Icon-only button with size presets and selection state, built on top of
 * `BasicIconButton`.
 *
 * Use `size` to apply one of the predefined sizing classes (`SIZES` enum).
 * The `selected` prop toggles a visual selection indicator and automatically
 * sets `aria-pressed` so assistive technologies announce the toggle state.
 * Use `flat` for a borderless/background-less appearance.
 *
 * All other props (including `iconProps`, `loader`, `disabled`, `onClick`) are
 * passed through to `BasicIconButton`.
 *
 * ## Accessibility
 * - `selected` maps directly to `aria-pressed` for toggle buttons.
 * - Always provide an `aria-label` describing the action (e.g. `"Close"`,
 *   `"Copy link"`), since the button contains only an icon with no visible text.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-icon-button--default-icon-buttons-template}
 */
export function IconButton(props: IconButtonProps) {
    const {
        className,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        mix,
        flat,
        size = SIZES.NONE,
        selected,
        ...otherProps
    } = props;

    const iconButtonClasses = classNames(
        'icon-button_button',
        className,
        mix,
        SIZES_CLASSES[size],
        {
            'icon-button_selected': selected,
            'icon-button_flat': flat,
        },
    );

    return (
        <BasicIconButton
            aria-pressed={selected !== undefined ? selected : undefined}
            {...otherProps}
            className={iconButtonClasses}
        />
    );
}
